import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import ToolLogo from '../ui/ToolLogo';
import { LogoSlug } from '../../data/logos';

type Accent = 'cyan' | 'magenta' | 'green' | 'purple' | 'amber';

interface StackGroup {
  category: string;
  tag: string;
  accent: Accent;
  /** Tools with a real brand mark in /public/assets/logos. */
  logos: LogoSlug[];
  /** Capabilities that have no logo — rendered as plain chips. */
  extras?: string[];
}

const accentColor: Record<Accent, string> = {
  cyan: '#22e6ff',
  magenta: '#ff3df0',
  green: '#2bffb0',
  purple: '#9d5cff',
  amber: '#ffb13d',
};

const stack: StackGroup[] = [
  {
    category: 'AI & Agentic Systems',
    tag: 'intelligence',
    accent: 'green',
    logos: ['anthropic', 'openai', 'googlegemini', 'langchain', 'elevenlabs', 'huggingface'],
    extras: ['Claude Certified Developer', 'Agent development', 'Tool calling', 'MCP servers', 'Context engineering', 'RAG', 'Prompt guardrails', 'Voice & chat agents'],
  },
  {
    category: 'Full-Stack & APIs',
    tag: 'code',
    accent: 'purple',
    logos: ['python', 'typescript', 'react', 'fastapi', 'flask', 'nodedotjs', 'nextdotjs', 'tailwindcss'],
    extras: ['REST API design', 'async/await', 'JWT / OAuth', 'Role-based access', 'Automated testing'],
  },
  {
    category: 'Data & Storage',
    tag: 'persistence',
    accent: 'amber',
    logos: ['postgresql', 'mongodb', 'redis', 'supabase', 'airtable', 'googlesheets'],
    extras: ['ETL pipelines', 'Versioned caching', 'SQL'],
  },
  {
    category: 'Automation Platforms',
    tag: 'orchestration',
    accent: 'magenta',
    logos: ['n8n', 'make', 'zapier'],
    extras: ['Webhooks', 'Cron & schedules', 'Self-hosted n8n', 'Retries & error branches'],
  },
  {
    category: 'CRM & GoHighLevel',
    tag: 'ghl',
    accent: 'cyan',
    logos: ['gohighlevel', 'twilio', 'calendly', 'hubspot'],
    extras: ['Pipelines', 'Funnels & forms', 'Snapshots', 'A2P 10DLC'],
  },
  {
    category: 'Infra & DevOps',
    tag: 'devops',
    accent: 'cyan',
    logos: ['docker', 'vercel', 'netlify', 'googlecloud', 'amazonaws', 'git', 'github'],
    extras: ['Linux / VPS', 'Cloudflare R2', 'Signed commits'],
  },
  {
    category: 'Commerce & Billing',
    tag: 'revenue',
    accent: 'green',
    logos: ['stripe', 'shopify', 'wordpress'],
    extras: ['Subscriptions & trials', 'Usage metering', 'Webhook-driven state'],
  },
  {
    category: 'Comms & Growth',
    tag: 'reach',
    accent: 'magenta',
    logos: ['whatsapp', 'telegram', 'slack', 'gmail', 'meta', 'instagram', 'googleads', 'googleanalytics'],
    extras: ['GTM & Meta Pixel', 'Technical SEO', 'Resend email'],
  },
  {
    category: 'Data Extraction & Vision',
    tag: 'scraping',
    accent: 'purple',
    logos: ['selenium', 'opencv', 'tensorflow', 'notion'],
    extras: ['Apify', 'Zillow API', 'DeepFace', 'PDF parsing'],
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

const SkillsSection: React.FC = () => {
  const { themeConfig } = useTheme();

  return (
    <section id="stack" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6 bg-neon-purple/60" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-purple">
            {'// the toolbox'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          My engineering <span className="gradient-text">stack</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          The models, frameworks, datastores and platforms I use to ship AI
          products, SaaS and automations. Not a wishlist: these are the tools
          running in production right now.
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-100px' }}
      >
        {stack.map((group) => {
          const c = accentColor[group.accent];
          return (
            <motion.div key={group.category} variants={item} className="n8n-node p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="font-display text-lg font-semibold leading-tight text-ink-main">
                  {group.category}
                </h3>
                <span
                  className="shrink-0 font-mono text-[10px] uppercase tracking-wider"
                  style={{ color: c }}
                >
                  {group.tag}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {group.logos.map((slug) => (
                  <ToolLogo key={slug} slug={slug} size={18} />
                ))}
              </div>

              {group.extras && (
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-canvas-border/50 pt-3">
                  {group.extras.map((skill) => (
                    <span
                      key={skill}
                      className="rounded-lg border bg-canvas-deep/60 px-2.5 py-1 font-mono text-[11px] text-ink-soft transition-colors hover:text-ink-main"
                      style={{ borderColor: `${c}33` }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};

export default SkillsSection;
